import axios, { AxiosError } from "axios";
import addMinutes from "date-fns/addMinutes";
import { MapMarker } from "../features/map/mapTypes";
import { messgroesse, messstelle, messwertLogGeflaggt, messwertLog } from "./apiTypes";
import { getMessstellenLookup } from "../features/messstellenLookup/messstellenLookupSlice";
import { GridCellValue } from "@material-ui/data-grid";
import { graphLine, trendsItem } from "../routes/analyse/trends/trendsTypes";
import { CustomApexAxisChartSeries, LineChartData, MesswerteWithSinglePoints } from "../features/chart/chartTypes";

export function convertDateToWeekNumber(date: Date): number {
  const oneJan = new Date(date.getFullYear(), 0, 1);
  const numberOfDays = Math.floor((date.getTime() - oneJan.getTime()) / (24 * 60 * 60 * 1000));
  return Math.ceil((date.getDay() + 1 + numberOfDays) / 7);
}

export function round4Digits(doubleNumber: number): number {
  return Math.round((doubleNumber + Number.EPSILON) * 10000) / 10000;
}

/* eslint-disable @typescript-eslint/no-explicit-any, security/detect-object-injection */
export function exportToCsvLocalTime(filename: string, rows: any[]): void {
  //https://stackoverflow.com/a/24922761
  const processRow = function (row: any) {
    let finalVal = "";
    for (let j = 0; j < row.length; j++) {
      let innerValue = row[j] === null ? "" : row[j].toString();
      if (row[j] instanceof Date) {
        innerValue = row[j].toLocaleDateString();
      }
      let result = innerValue.replace(/"/g, '""');
      if (result.search(/("|,|\n)/g) >= 0) result = '"' + result + '"';
      if (j > 0) finalVal += ",";
      finalVal += result;
    }
    return finalVal + "\n";
  };

  let csvFile = "";
  for (let i = 0; i < rows.length; i++) {
    csvFile += processRow(rows[i]);
  }

  const blob = new Blob([csvFile], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  if (link.download !== undefined) {
    // feature detection
    // Browsers that support HTML5 download attribute
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}
/* eslint-enable @typescript-eslint/no-explicit-any, security/detect-object-injection */

export function logError(error: Error | AxiosError): void {
  if (axios.isAxiosError(error)) {
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      console.debug(error.response.data);
      console.debug(error.response.status);
      console.debug(error.response.headers);
    } else if (error.request) {
      // The request was made but no response was received
      // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
      // http.ClientRequest in node.js
      console.debug(error.request);
    } else {
      // Something happened in setting up the request that triggered an Error
      console.debug("Error", error.message);
    }
    console.debug(error.config);
  } else {
    console.debug(error);
  }
}

export function nanosecondsTimeStampToLocalString(timestamp: number): string {
  return new Date(timestamp / 1000000).toLocaleString() + "." + timestamp.toString().slice(-9);
}

export function nanosecondsTimeStampToLocalStringWithoutSlice(timestamp: number): string {
  return new Date(timestamp / 1000000).toLocaleString();
}

export function nanosecondsTimeStampToUTCStringWithoutSlice(timestamp: number): string {
  return toUTCDateString(new Date(timestamp / 1000000));
}

export function nanosecondsTimeStampToUTCString(timestamp: number): string {
  return toUTCDateString(new Date(timestamp / 1000000)) + "." + timestamp.toString().slice(-9);
}

export function nanosecondsTimeStampToDate(timestamp: number): Date {
  return new Date(timestamp / 1000000);
}

export function nsTSToLocalStringWithoutNS(timestamp: number): string {
  return new Date(timestamp / 1000000).toLocaleString();
}
export function nsTSToUTCStringWithoutNS(timestamp: number): string {
  const date = new Date(timestamp / 1000000);
  return toUTCDateString(date);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isDataWithNanoseconds(data: any[]): boolean {
  return data.find((e) => e.zeit.toString().slice(-6) !== "000000") ? true : false;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isDataWithNanosecondsUTC(data: any[]): boolean {
  return data.find((e) => e.zeit.toUTC().slice(-6) !== "000000") ? true : false;
}

export function addTimezoneOffset(value: Date): Date {
  const date = value instanceof Date ? value : new Date(value);
  const diff = date.getTimezoneOffset();
  return addMinutes(date, diff);
}

export function subtractTimezoneOffset(value: Date): Date {
  const date = value instanceof Date ? value : new Date(value);
  const diff = date.getTimezoneOffset();
  return addMinutes(date, -diff);
}

export function toUTCDateString(date: Date): string {
  const timeDiff = date.getTimezoneOffset() * 60000;
  const adjustedDate = new Date(date.valueOf() + timeDiff);
  const hours = date.getUTCHours();
  const min = date.getUTCMinutes();
  const sec = date.getUTCSeconds();
  return (
    adjustedDate.toLocaleDateString() +
    ", " +
    (hours.toString().length > 1 ? hours.toString() : "0" + hours.toString()) +
    ":" +
    (min.toString().length > 1 ? min.toString() : "0" + min.toString()) +
    ":" +
    (sec.toString().length > 1 ? sec.toString() : "0" + sec.toString())
  );
}

export function toUTCDateStringWithoutTime(date: Date): string {
  const timeDiff = date.getTimezoneOffset() * 60000;
  const adjustedDate = new Date(date.valueOf() + timeDiff);
  return adjustedDate.toLocaleDateString();
}

/* eslint-disable @typescript-eslint/no-explicit-any, security/detect-object-injection */
export function getYAxisTitleText(data: any[], messgroesseOptions: messgroesse[]): string {
  return Object.keys(data[0])
    .filter((x) => x !== "zeit")
    .map((key) => key + " [" + messgroesseOptions.find((x) => x.feldname == key)?.einheit + "]")
    .join(", ");
}
/* eslint-enable @typescript-eslint/no-explicit-any, security/detect-object-injection */

/* eslint-disable @typescript-eslint/no-explicit-any, security/detect-object-injection */
export function transformDataToApexAxisChartSeries(data: any[], messgroesseOptions: messgroesse[], isNanoseconds: boolean, lineColors: string[]): ApexAxisChartSeries {
  return Object.keys(data[0])
    .filter((x) => x !== "zeit")
    .map((key, index) => ({
      name: messgroesseOptions.find((x) => x.feldname == key)?.beschreibung + " " + key,
      // eslint-disable-next-line security/detect-object-injection
      color: lineColors[index],
      data: data.map((x) => ({
        x: isNanoseconds ? x.zeit : new Date(x.zeit / 1000000),
        y: x[key],
      })),
    }));
}
/* eslint-enable @typescript-eslint/no-explicit-any, security/detect-object-injection */

export function fillGapsWithNull(data: any[], dataClass: string): MesswerteWithSinglePoints {
  const result: MesswerteWithSinglePoints = {
    data: data,
    singlePoints: [],
  };
  let interval = 0;
  let toleranceFactor = 0;
  switch (dataClass) {
    case "c2h":
      interval = 2 * 60 * 60 * 1000 * 1000 * 1000;
      break;
    case "c10m":
      interval = 10 * 60 * 1000 * 1000 * 1000;
      break;
    case "c10s":
      interval = 10 * 1000 * 1000 * 1000;
      break;
    case "creca":
      interval = 100 * 1000;
      toleranceFactor = 4;
      break;
    case "crecb":
      interval = 10 * 1000 * 1000;
      toleranceFactor = 4;
      break;
    default:
      return result;
  }

  const actualTimeSpan = data[data.length - 1].zeit - data[0].zeit;
  const expectedTimeSpan = (data.length - 1) * interval;
  const maximumToleratedDevitation = interval * toleranceFactor;
  if (Math.abs(actualTimeSpan - expectedTimeSpan) <= maximumToleratedDevitation) return result;

  const filledData = [];

  for (let i = 0; i < data.length - 1; i++) {
    filledData.push(data[i]);
    const nextTime = data[i].zeit + interval;
    if (nextTime < data[i + 1].zeit) {
      const fillIn = { ...data[i] };
      Object.keys(fillIn).forEach((x) => (fillIn[x] = null));
      fillIn.zeit = nextTime;
      filledData.push(fillIn);
      // if (i + 1 == data.length - 1 || data[i + 1].zeit + interval < data[i + 2].zeit) {
      //   result.singlePoints.push(filledData.length);
      // }
    }
  }

  result.data = filledData;

  return result;
}

/* eslint-disable @typescript-eslint/no-explicit-any, security/detect-object-injection */
export const createDictionaryArray = <T, K extends keyof any>(list: T[], getKey: (item: T) => K): Record<K, T[]> =>
  list.reduce((previous, currentItem) => {
    const group = getKey(currentItem);
    if (!previous[group]) previous[group] = [];
    previous[group].push(currentItem);
    return previous;
  }, {} as Record<K, T[]>);
/* eslint-enable @typescript-eslint/no-explicit-any, security/detect-object-injection */

/* eslint-disable @typescript-eslint/no-explicit-any, security/detect-object-injection */
export function transformDataToApexAxisChartSeriesMesswerte(
  messwerte: messwertLog[],
  messwerteGeflaggt: messwertLogGeflaggt[],
  startDatepicker: Date,
  endeDatepicker: Date,
  messstellenDict: Record<string, messstelle>
): any {
  const seriesData: any = [];
  initSeriesDataForMessstelle(seriesData);

  if (messwerte.length > 0) {
    const groupedMesswerte = createDictionaryArray(messwerte, (mw) => mw.messstelle);
    //const groupedMesswerteGeflaggt = createDictionaryArray(messwerteGeflaggt, mw => mw.messstelle);
    for (const currentMessstelle in groupedMesswerte) {
      const allDataOfMessstelle = groupedMesswerte[currentMessstelle];
      for (let indexMessstelle = 0; allDataOfMessstelle.length > indexMessstelle; indexMessstelle++) {
        //Eigenschaften für Messtelle ohne Flag
        const data = allDataOfMessstelle[indexMessstelle];
        const entryStart = nanosecondsTimeStampToDate(data.start);
        const entryEnd = nanosecondsTimeStampToDate(data.ende);
        const zeitraum = getZeitraum(entryStart, entryEnd, startDatepicker, endeDatepicker);
        const umspannwerk: string = getMessstellenLookup(messstellenDict, data.messstelle, "umspannwerk");
        const schaltfeld: string = getMessstellenLookup(messstellenDict, data.messstelle, "schaltfeld");

        seriesData[0].data.push({
          x: umspannwerk + " " + schaltfeld + " " + currentMessstelle,
          y: [zeitraum[0].getTime(), zeitraum[1].getTime()],
        });
        //}
        //Keine Daten
        if (allDataOfMessstelle.length - 1 > indexMessstelle) {
          const dataNext = allDataOfMessstelle[indexMessstelle + 1];
          const zeitraumNext = getZeitraum(nanosecondsTimeStampToDate(dataNext.start), nanosecondsTimeStampToDate(dataNext.ende), startDatepicker, endeDatepicker);
          if (zeitraumNext[1] <= zeitraum[0]) {
            seriesData[1].data.push({
              x: umspannwerk + " " + schaltfeld + " " + currentMessstelle,
              y: [zeitraumNext[1].getTime(), zeitraum[0].getTime()],
            });
          }
        }
      }
    }
    for (let indexMessstelleGeflaggt = 0; messwerteGeflaggt.length > indexMessstelleGeflaggt; indexMessstelleGeflaggt++) {
      const data = messwerteGeflaggt[indexMessstelleGeflaggt];
      const entryStart = nanosecondsTimeStampToDate(data.start);
      const entryEnd = nanosecondsTimeStampToDate(data.ende);
      const zeitraum = getZeitraum(entryStart, entryEnd, startDatepicker, endeDatepicker);
      const umspannwerk: string = getMessstellenLookup(messstellenDict, data.messstelle, "umspannwerk");
      const schaltfeld: string = getMessstellenLookup(messstellenDict, data.messstelle, "schaltfeld");
      seriesData[2].data.push({
        x: umspannwerk + " " + schaltfeld + " " + data.messstelle,
        y: [zeitraum[0].getTime(), zeitraum[1].getTime()],
      });
    }
  }

  return seriesData;
}
/* eslint-enable @typescript-eslint/no-explicit-any, security/detect-object-injection */

/* eslint-disable @typescript-eslint/no-explicit-any, security/detect-object-injection */
function initSeriesDataForMessstelle(seriesData: any) {
  seriesData.push({
    name: "Aufgezeichnete Daten",
    data: [],
  });
  seriesData.push({
    name: "Keine Daten",
    data: [],
  });
  seriesData.push({
    name: "Fehler",
    data: [],
  });
}
/* eslint-enable @typescript-eslint/no-explicit-any, security/detect-object-injection */

function getZeitraum(graphStart: Date, graphEnde: Date, start: Date, ende: Date) {
  if (graphStart < start) {
    return graphEnde > ende ? [start, ende] : [start, graphEnde];
  } else return graphEnde < ende ? [graphStart, graphEnde] : [graphStart, ende];
}

export function transformMessstelleToMapMarkers(messpunkte: messstelle[]): MapMarker[] {
  const result: MapMarker[] = [];
  messpunkte.forEach((e) => {
    result.push({ name: e.name, breitengrad: e.breitengrad, laengengrad: e.laengengrad });
  });
  return result;
}

export function dateDiffInDaysUTC(a: Date, b: Date): number {
  //https://stackoverflow.com/questions/3224834/get-difference-between-2-dates-in-javascript
  const _MS_PER_DAY = 1000 * 60 * 60 * 24;
  const utc1 = Date.UTC(a.getFullYear(), a.getMonth(), a.getDate());
  const utc2 = Date.UTC(b.getFullYear(), b.getMonth(), b.getDate());

  return Math.floor((utc2 - utc1) / _MS_PER_DAY);
}

export function dateDiffInDays(a: Date, b: Date): number {
  const _MS_PER_DAY = 1000 * 60 * 60 * 24;
  const startDate = new Date(a.getFullYear(), a.getMonth(), a.getDate()).getTime();
  const endDate = new Date(b.getFullYear(), b.getMonth(), b.getDate()).getTime();

  return Math.floor((endDate - startDate) / _MS_PER_DAY);
}

export function isValidDate(d: Date): boolean {
  return !isNaN(d.getTime());
}

/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/explicit-module-boundary-types, security/detect-object-injection */
export function addToObject(obj: any, key: string, value: unknown, index: number): Record<string, unknown> {
  const temp: Record<string, unknown> = {};
  let i = 0;

  for (const prop in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, prop)) {
      if (i === index && key && value) {
        temp[key] = value;
      }
      temp[prop] = obj[prop];
      i++;
    }
  }

  // If no index, add to the end
  if (!index && key && value) {
    temp[key] = value;
  }

  return temp;
}

export function alphaNumSort(v1: GridCellValue, v2: GridCellValue): number {
  return (v1 as string).localeCompare(v2 as string, "de", { numeric: true });
}
/* eslint-enable @typescript-eslint/no-explicit-any, @typescript-eslint/explicit-module-boundary-types, security/detect-object-injection */

export function trendsDataToLineChartData(lineName: string, values: trendsItem[], title: string, color: string): LineChartData {
  const apexChartsSeries: CustomApexAxisChartSeries = [{ name: `${lineName}`, color: color, data: values.map((item) => ({ x: item.zeit, y: item.wert })) }];

  const graphEntry: LineChartData = {
    yAxisTitleText: "",
    isNanoseconds: false,
    title: title,
    series: apexChartsSeries,
    ereignisse: [],
    showEreignisse: false,
    geflaggteMesswerte: [],
    singlePoints: [],
  };

  return graphEntry;
}

export function trendsDataToGraphLine(values: trendsItem[]): graphLine[] {
  const data = values.map((item) => ({ x: item.zeit, y: item.wert }));

  return data;
}
